import React, { useContext, useEffect, useMemo, useState } from 'react';
import t from 'react-translate';
import moment from 'moment';
import { useAppDispatch } from 'redux/store';
import { setUser, updateUser } from 'redux/actions/users';
import { useSelector } from 'react-redux';
import { AngularContext, AngularServicesContext } from 'react-app';
import { getCurrentUser, getUserTimezone } from 'redux/selectors/users';

import NvTimezoneSelector from 'shared/components/nv-timezone-selector';
import NvRadioButton from 'shared/components/inputs/nv-radio-button';
import DisplayTimeZoneInNavSwitch from '../display-timezone-in-nav-switch';


const TimezonePreferences = () => {
  const { CurrentUserManager } = useContext(AngularServicesContext);
  const { injectServices } = useContext(AngularContext);
  const [TimezonesService] = injectServices(['TimezonesService']);
  const dispatch = useAppDispatch();

  const currentUser = useSelector(getCurrentUser);
  const currentUserTimezone = useSelector(getUserTimezone);
  const timezones = useMemo(() => TimezonesService.timezones || [], [TimezonesService.timezones]);
  const [isAutomatic, setIsAutomatic] = useState(true);
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [lastSelectedShortName, setLastSelectedShortName] = useState(null);

  useEffect(() => {
    const tzValue = currentUser.timeZone;
    const isAuto = !tzValue || tzValue.trim() === '';
    setIsAutomatic(isAuto);

    if (isAuto) {
      setSelectedTimezone(null);
    } else {
      let latestSelectedTz = timezones.find((tz) => tz.longName === tzValue && tz.shortName === lastSelectedShortName);
      // If not found, fall back to just longName match
      if (!latestSelectedTz) {
        latestSelectedTz = timezones.find((tz) => tz.longName === tzValue) || null;
      }
      setSelectedTimezone(latestSelectedTz);
    }
  }, [currentUser.timeZone, timezones, lastSelectedShortName]);

  const updateTimezone = (timezone, displayTimeZoneClock = null) => {
    const isAuto = timezone === null;
    const shortTzName = isAuto ? '' : timezone.shortName;
    const longTzName = isAuto ? '' : timezones.find(tz => tz.shortName === shortTzName)?.longName;
    dispatch(
      updateUser({
        timeZone: shortTzName,
        displayTimeZoneClock,
      }),
    ).then(() => {
      CurrentUserManager.user.updateFromReact({
        timeZone: longTzName,
        displayTimeZoneClock,
      });
    });
  };

  const handleAutomaticChange = () => {
    setIsAutomatic(true);
    setSelectedTimezone(null);
    updateTimezone(null, currentUser.displayTimeZoneClock);
  };

  const handleManualChange = () => {
    setIsAutomatic(false);
    let effectiveTz = selectedTimezone;
    if (!effectiveTz) {
      const guessed = guessTimezone();
      setSelectedTimezone(guessed);
      effectiveTz = guessed;
    }
    updateTimezone(effectiveTz, true);
  };

  const guessTimezone = () => {
    //  Try to guess the timezone from the browser
    const guessString = TimezonesService.correctSpelling(currentUser.timeZone || moment.tz.guess());
    return timezones.find((tz) => tz.longName === guessString) || timezones[0];
  };

  const onTimezoneSelected = (tz, displayTimeZoneClock = true) => {
    setIsAutomatic(false);
    setSelectedTimezone(tz);
    setLastSelectedShortName(tz.shortName);
    updateTimezone(tz, displayTimeZoneClock);
  };

  return (
    <div>
      <div className='header-row page-subtitle'>{t.ACCOUNT_SETTINGS.LANGS_AND_TZ.TIME_ZONE_HEADER()}</div>
      <div className='timezone-description text-regular'>{t.ACCOUNT_SETTINGS.LANGS_AND_TZ.TIME_ZONE_DESCRIPTION()}</div>
      <div role='radiogroup' aria-labelledby='timezone_label'>
        <div className='radio'>
          <input
            type='radio'
            name='tzOptions'
            id='automaticTZ'
            checked={isAutomatic}
            onChange={handleAutomaticChange}
          />
          <label htmlFor='automaticTZ'>{t.ACCOUNT_SETTINGS.LANGS_AND_TZ.AUTO_UPDATE_TZ()}</label>
        </div>
        <div className='text-regular manual-tz'>{t.ACCOUNT_SETTINGS.LANGS_AND_TZ.MANUAL_TZ()}</div>
        <NvRadioButton
          className='d-flex pb-2'
          value='manualTZ'
          name='tzOptions'
          checked={!isAutomatic}
          onChange={handleManualChange}
          label=''
          customLabel={() => (
            <div>
              <NvTimezoneSelector
                selectedTimezone={selectedTimezone}
                onchange={(tz) => onTimezoneSelected(tz, true)}
                placeholder={isAutomatic ? `${t.ACCOUNT_SETTINGS.LANGS_AND_TZ.SELECT_A_TIME_ZONE()}` : undefined}
              />
            </div>
          )}
        />
        <div>
          <DisplayTimeZoneInNavSwitch />
        </div>
      </div>
    </div>
  );
};

export default TimezonePreferences;
